import { api } from "./client";
import type { FolderDTO, FolderIndexDTO, BreadcrumbItem } from "@titancloud/shared";

export async function listFolders(parentId?: string | null): Promise<FolderDTO[]> {
  const qs = parentId ? `?parentId=${parentId}` : "";
  return api.get(`/folders${qs}`);
}

/** Every folder of the current user, so folder navigation needs no request per click. */
export async function listAllFolders(): Promise<FolderIndexDTO> {
  return api.get("/folders/all");
}

export async function createFolder(
  name: string,
  parentId: string | null
): Promise<FolderDTO> {
  return api.post("/folders", { name, parentId });
}

/** Folder ids in subtrees of the given folders (for move destination blocking). */
export async function fetchSubtreeMembers(folderIds: string[]): Promise<string[]> {
  if (folderIds.length === 0) return [];
  const data = await api.post<{ ids: string[] }>("/folders/subtree-members", {
    folderIds,
  });
  return data.ids;
}

/**
 * Find-or-create every folder needed to realize a set of relative directory
 * paths under `parentId` (used when uploading a dropped folder tree so its
 * structure is preserved instead of flattened).
 */
export async function resolveFolderTree(
  parentId: string | null,
  paths: string[]
): Promise<Record<string, string>> {
  if (paths.length === 0) return {};
  const data = await api.post<{ folderIds: Record<string, string> }>(
    "/folders/resolve-tree",
    { parentId, paths }
  );
  return data.folderIds;
}

export async function getFolderTree(folderId: string): Promise<BreadcrumbItem[]> {
  return api.get(`/folders/${folderId}/tree`);
}

export async function renameFolder(folderId: string, name: string): Promise<FolderDTO> {
  return api.patch(`/folders/${folderId}`, { name });
}

export async function softDeleteFolder(folderId: string): Promise<void> {
  await api.delete(`/folders/${folderId}`);
}

/** Trigger a ZIP download of an entire folder (streamed from the server) */
export function downloadFolderZip(folderId: string, folderName: string): void {
  const a = document.createElement("a");
  a.href = `/api/folders/${folderId}/download`;
  a.download = `${folderName}.zip`;
  a.click();
}
